import type { Logger } from "@expert/logging";

import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { type ChallengeInfo, useChallenges, WidgetContainer } from "@expert/sales-tools";
import { Flex } from "@mantine/core";
import { useMemo } from "react";
import { withErrorBoundary } from "react-error-boundary";

import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";
import type { TrainingTask } from "@/sdk";

import { CallControlsManager } from "@/call-controls";
import { useShouldShowCallControls } from "@/call-controls/hooks/useShouldShowCallControls";
import { TrainingModal } from "@/components/training";
import { ExpertUniversityHome } from "@/expert-university";
import { usePlatformGuideManager } from "@/guides";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useActiveTask, useAgentStore, useCallbackState, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { DeprecatedSessionSummary, SessionSummary } from "@/session-summary";
import { createFallbackRender, RenderError } from "@/shared-utils";
import { Sidecar } from "@/sidecar/Sidecar";
import { RedesignedTranscript } from "@/transcript/redesigned/RedesignedTranscript";

import { WorkspaceLayout } from "./redesigned-layout/WorkspaceLayout";
import workspaceStyles from "./workspace.module.css";

interface VoiceWorkspaceContentRedesignedProps {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    activeTrainingTask: TrainingTask | null;
    /** Partner-level toggle for whether the customer panel exists at all. */
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasActiveVoiceTask: boolean;
    logger: Logger;
    sessionId: string;
    showExpertUniversity: undefined | boolean;
    startedTimestamp: number;
}

const TranscriptHandled = withErrorBoundary(RedesignedTranscript, { FallbackComponent: RenderError });

/** Redesigned voice workspace content (gated by `isVoiceWorkspaceRedesignEnabled`). */
export function VoiceWorkspaceContentRedesigned({
    activeExpertUniversityTask,
    activeTrainingTask,
    customerPanelEnabled,
    customerSession,
    hasActiveVoiceTask,
    logger,
    sessionId,
    showExpertUniversity,
    startedTimestamp,
}: VoiceWorkspaceContentRedesignedProps) {
    const activeTask = useActiveTask();
    const activeVoiceTask = useMemo(() => (isVoiceTask(activeTask) ? activeTask : null), [activeTask]);

    const callControlsLogger = useLogger({ module: "VoiceWorkspaceContentRedesigned" });
    const CallControlsManagerHandled = useMemo(
        () => withErrorBoundary(CallControlsManager, { fallbackRender: createFallbackRender(callControlsLogger) }),
        [callControlsLogger],
    );

    const TrainingModalHandled = useMemo(
        () => withErrorBoundary(TrainingModal, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );

    const SessionSummaryHandled = useMemo(
        () => withErrorBoundary(SessionSummary, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );

    const DeprecatedSessionSummaryHandled = useMemo(
        () => withErrorBoundary(DeprecatedSessionSummary, { fallbackRender: createFallbackRender(logger) }),
        [logger],
    );

    const { areGuidesAvailable, guide, guideToolboxCommand, isGuideOpen, closeGuide } = usePlatformGuideManager({
        isCustomerSession: customerSession,
        sessionId,
        logger,
    });
    // Take open() from guideToolboxCommand (not the raw openGuide) so the redesign keeps its
    // error-toast fallback when no guide is available to launch.
    const { open: openGuide } = guideToolboxCommand;

    // Mirrors WorkspaceBody's end-of-call wiring: on a customer-session wrapping state the summary
    // modal auto-opens; submitting clears the task so the call bar collapses. Without it the call bar
    // stays stuck until the agent's next activity change side-effect-clears the task.
    const session = useSession();
    const callbackState = useCallbackState();
    const currentActivity = useAgentStore((state) => state.activity);
    const { simplifiedSummary } = usePartnerFeatureConfigured();
    const { wrappingAgentActivity, leaderboards, pacingTargets } = usePartnerConfig();
    const {
        features: { isUpdatedEndSessionFlowEnabled, isChallengeEnabled, isPacingEnabled },
    } = useFeatures<WorkspaceFeatures>();
    // Single source of truth: the `customerSession` prop is itself `session.kind === "with-customer"`
    // (set in Workspace.tsx), so reuse it rather than re-deriving and risking divergence.
    const isCustomerSession = customerSession;
    const hasPendingCallback = callbackState?.callbackType === "CallbackNow";
    const hasActiveCustomerSessionWrappingState = isCustomerSession && !!session.wrappingState;
    const isWrappingState =
        currentActivity === wrappingAgentActivity ||
        activeTask?.status === "wrapping" ||
        hasActiveCustomerSessionWrappingState;
    const displaySummary = isCustomerSession && isWrappingState && !hasPendingCallback;

    const { data: challengeData } = useChallenges();
    const hasEnrollmentChallenges =
        isChallengeEnabled &&
        challengeData?.some((c: ChallengeInfo) => c.challengeType === "solo" || c.challengeType === "team");

    const hasWidgets =
        leaderboards.length > 0 || !!hasEnrollmentChallenges || (isPacingEnabled && !!pacingTargets?.enabled);

    const expertUniversityActive = Boolean(showExpertUniversity);
    const trainingTaskActive = Boolean(activeTrainingTask?.startTimestamp);

    // Treat the voice task as live only when it's not in a terminal state. Stale tasks
    // left behind in `completed` or `cancelled` would otherwise keep the call bar open
    // (and showing the customer's MDN) long after the call has actually ended.
    const hasLiveVoiceTask =
        hasActiveVoiceTask &&
        activeVoiceTask !== null &&
        activeVoiceTask.status !== "completed" &&
        activeVoiceTask.status !== "cancelled";

    // Show the leftTop call-controls panel only when the bar has something to render
    // (see useShouldShowCallControls) and we're not in Expert University. Without this gate the
    // panel opens around an empty CallControlsManager — an empty black bar.
    const shouldShowCallControls = useShouldShowCallControls();
    const leftTopOpen = !expertUniversityActive && shouldShowCallControls;

    const leftSidebarContent = <WidgetContainer />;

    const topbarContent = (
        <Flex pos="absolute" inset={0}>
            <CallControlsManagerHandled voiceTask={hasLiveVoiceTask ? activeVoiceTask : null} isCompact />
        </Flex>
    );

    const leftMainContent = (
        <Flex pos="absolute" inset={0}>
            {expertUniversityActive ? (
                <ExpertUniversityHome />
            ) : trainingTaskActive && activeTrainingTask ? (
                <Flex className={workspaceStyles.workspaceBody} direction="row" m="2.5rem 3rem">
                    <TrainingModalHandled task={activeTrainingTask} />
                </Flex>
            ) : (
                <TranscriptHandled
                    key={hasLiveVoiceTask ? activeVoiceTask?.id : "idle"}
                    taskStartTimeInMs={startedTimestamp}
                />
            )}
        </Flex>
    );

    // Mirrors the legacy WorkspaceContentRightSidebarSlot gate: show the customer panel for a real
    // session only when the partner has it enabled, but always during an Expert University training
    // sim (which uses simulated data, so the partner toggle doesn't apply).
    const showCustomerPanel = (customerSession && customerPanelEnabled) || Boolean(activeExpertUniversityTask);

    const rightMainContent = (
        <Sidecar
            customerSession={showCustomerPanel}
            openGuide={openGuide}
            closeGuide={closeGuide}
            guide={guide}
            areGuidesAvailable={areGuidesAvailable}
            isGuideOpen={isGuideOpen}
        />
    );

    return (
        <>
            <WorkspaceLayout
                leftSidebar={leftSidebarContent}
                leftSidebarOpen={hasWidgets && !expertUniversityActive}
                leftSidebarHidden={customerSession || hasLiveVoiceTask}
                leftTop={topbarContent}
                leftTopOpen={leftTopOpen}
                leftBottom={leftMainContent}
                rightTop={rightMainContent}
                rightTopOpen={!expertUniversityActive}
            />
            {displaySummary &&
                (isUpdatedEndSessionFlowEnabled && !simplifiedSummary ? (
                    <SessionSummaryHandled />
                ) : (
                    <DeprecatedSessionSummaryHandled />
                ))}
        </>
    );
}
